import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { 
  Link2, Plus, X, FileText, ExternalLink, Loader2 
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

interface LinkedDocument {
  id: string;
  title: string;
  type?: string;
  updatedAt?: string;
}

interface LinkedDocumentsProps {
  documentId: string;
  className?: string;
}

export function LinkedDocuments({ documentId, className = "" }: LinkedDocumentsProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isAddingLink, setIsAddingLink] = useState(false);

  const { data: linkedDocs = [], isLoading } = useQuery<LinkedDocument[]>({
    queryKey: ["/api/knowledgebase", documentId, "links"],
    queryFn: async () => {
      const res = await fetch(`/api/knowledgebase/${documentId}/links`);
      if (!res.ok) throw new Error("Failed to fetch linked documents");
      return res.json();
    },
  });

  const { data: allDocs = [] } = useQuery<any[]>({
    queryKey: ["/api/knowledgebase"],
    enabled: isAddingLink,
  });

  const linkMutation = useMutation({
    mutationFn: async (linkedDocumentId: string) => {
      const res = await apiRequest("POST", `/api/knowledgebase/${documentId}/links`, {
        linkedDocumentId,
      });
      return res.json();
    },
    onSuccess: (_, linkedDocumentId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase", documentId, "links"] });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase", linkedDocumentId, "links"] });
      queryClient.invalidateQueries({ queryKey: ["/api/related-content"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["/api/search"], exact: false });
      toast({
        title: "Document linked",
        description: "The documents are now connected as related content.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to link",
        description: error.message || "Could not link the documents.",
        variant: "destructive",
      });
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: async (linkedId: string) => {
      const res = await apiRequest("DELETE", `/api/knowledgebase/${documentId}/links/${linkedId}`);
      return res.json();
    },
    onSuccess: (_, linkedId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase", documentId, "links"] });
      queryClient.invalidateQueries({ queryKey: ["/api/knowledgebase", linkedId, "links"] });
      queryClient.invalidateQueries({ queryKey: ["/api/related-content"], exact: false });
      queryClient.invalidateQueries({ queryKey: ["/api/search"], exact: false });
      toast({
        title: "Link removed",
        description: "The documents are no longer connected.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to unlink",
        description: error.message || "Could not remove the link.",
        variant: "destructive",
      });
    },
  });

  const availableDocs = allDocs.filter(
    (doc) => doc.id !== documentId && !linkedDocs.some((ld) => ld.id === doc.id)
  );

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            See Also
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            See Also
            {linkedDocs.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {linkedDocs.length}
              </Badge>
            )}
          </CardTitle>
          <Popover open={isAddingLink} onOpenChange={setIsAddingLink}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <Plus className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end">
              <div className="p-2 border-b">
                <h4 className="font-medium text-sm">Link to Document</h4>
                <p className="text-xs text-muted-foreground">
                  Select a document to connect
                </p>
              </div>
              <ScrollArea className="max-h-60">
                <div className="p-2 space-y-1">
                  {availableDocs.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No available documents
                    </p>
                  ) : (
                    availableDocs.map((doc) => (
                      <Button
                        key={doc.id}
                        variant="ghost"
                        className="w-full justify-start text-left h-auto py-2"
                        onClick={() => {
                          linkMutation.mutate(doc.id);
                          setIsAddingLink(false);
                        }}
                        disabled={linkMutation.isPending}
                        data-testid={`link-doc-${doc.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2 shrink-0" />
                        <span className="truncate">{doc.title || "Untitled"}</span>
                      </Button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0">
        {linkedDocs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No linked documents. Click + to add connections.
          </p>
        ) : (
          <div className="space-y-2">
            {linkedDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-2 p-2 rounded-md border hover-elevate group"
                data-testid={`linked-doc-${doc.id}`}
              >
                <div
                  className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                  onClick={() => setLocation(`/document/${doc.id}`)}
                >
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.title}</p>
                    {doc.updatedAt && (
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(doc.updatedAt), { addSuffix: true })}
                      </p>
                    )}
                  </div>
                  <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    unlinkMutation.mutate(doc.id);
                  }}
                  disabled={unlinkMutation.isPending}
                  data-testid={`unlink-doc-${doc.id}`}
                >
                  {unlinkMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
